import Immutable from 'immutable';
import invariant from 'invariant';
import { VariableJSON, Variable } from './Variable';
import Compute from './compute';

export interface BusinessPlanJSON {
  submission: number;
  submittedAt: Date | undefined;
  readOnly: boolean;
  variables: VariableJSON[]
}

export class BusinessPlan {
  #submission: number;
  #variables: Immutable.List<Variable>;
  submittedAt: Date | undefined;
  readOnly: boolean;

  constructor(
    submission: number,
    variables = Immutable.List<Variable>(),
    readOnly: boolean = false,
    submittedAt?: Date,
  ) {
    this.#submission = submission;
    this.#variables = variables;
    this.readOnly = readOnly;
    this.submittedAt = submittedAt;
  }

  get submission() {
    return this.#submission;
  }

  get variables() {
    return this.#variables.toArray();
  }

  addVariable(variable: Variable) {
    this.#variables = this.#variables.push(variable);
  }

  setVariable(variableName: string, year: number, value: number) {
    const variable = this.#variables.find(variable => (
      variable.name === variableName &&
      variable.year === year
    ));
    invariant(variable, `Variable [${variableName}] in year [${year}] does not exist!`);

    variable.setValue(value);
    return this;
  }

  getVariable(variableName: string, year: number) {
    const variable = this.#variables.find(variable => (
      variable.name === variableName &&
      variable.year === year
    ));
    invariant(variable, `Variable [${variableName}] in year [${year}] does not exist!`);

    return variable.value;
  }

  compute() {
    this.#variables = Immutable.List(
      Compute(this.#variables.toArray())
        .map(variable => new Variable(
          this.#submission,
          variable.name,
          variable.year,
          variable.value,
          variable.key,
          variable.disabled,
          variable.showInGui,
        ))
    );
    return this;
  }

  toJSON(): BusinessPlanJSON {
    return {
      submission: this.submission,
      submittedAt: this.submittedAt,
      readOnly: this.readOnly,
      variables: this.variables.map(variable => variable.toJSON())
    }
  }

  static fromJSON(json: BusinessPlanJSON): BusinessPlan {
    return new BusinessPlan(
      json.submission,
      Immutable.List(json.variables.map(variable => Variable.fromJSON(variable))),
      json.readOnly,
      json.submittedAt,
    );
  }
}
